import { useEffect, useRef, useState } from "react";
const NPSLogo = "/assets/NPSLogo.png";
import SignUpService from "@/services/NPS/Stepper/APIs/SignUpService";
import { useNavigate } from "react-router-dom";
import navigationRoutes from "@/utils/navigationRoutes";

const Signup = () => {
  const navigate = useNavigate();
  const [countryCode, setCountryCode] = useState<any>(null);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [countryCodes, setCountryCodes] = useState<any[]>([]);

  useEffect(() => {
    const fetchCountryCodes = async () => {
      const res = await SignUpService.getCountryCodes();
      if (res && Array.isArray(res)) {
        setCountryCodes(res);

        // ✅ Automatically set "IND" as default if available
        const india = res.find(c => c.label === "IND");
        if (india) {
          setCountryCode(india.value);
        }
      }
    };
    fetchCountryCodes();
  }, []);

  // ✅ Load mobile number from localStorage (prefill if exists)
  // useEffect(() => {
  //   const savedMobile = localStorage.getItem("userMobile");
  //   if (savedMobile) setMobile(savedMobile);
  // }, []);

  // ✅ Validation for Indian mobile numbers
  const validateMobile = (value: string) => {
    if (countryCode === "+91") {
      const indianRegex = /^[6-9]\d{9}$/;
      if (!indianRegex.test(value)) {
        setError("Please enter a valid 10-digit Indian mobile number.");
      } else {
        setError("");
      }
    } else {
      setError("");
    }
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ""); // allow digits only
    setMobile(value);
    validateMobile(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate mobile before saving
    const indianRegex = /^[6-9]\d{9}$/;
    if (countryCode === "+91" && !indianRegex.test(mobile)) {
      setError("Please enter a valid 10-digit Indian mobile number.");
      return;
    }

    setError("");

    // ✅ Save to localStorage
    localStorage.setItem("userMobile", mobile);

    navigate(navigationRoutes.NEW_SUBSCRIBER_REGISTRATION);
  };

  const handleSendOtp = async () => {
    const payload = {
      phone_number: mobile,
      phone_code: countryCode,
    };
    const res = await SignUpService.sendOtp(payload);
    if (res) {
      setOtp(res.otp);
    }
  };

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      {/* Left Section */}
      <div
        className="flex flex-1 flex-col bg-cover bg-center bg-no-repeat p-8 text-center"
        style={{ backgroundImage: "url('/assets/bg-login.png')" }}
      >
        <img className="w-35 mx-auto" src={NPSLogo} alt="Fincare Logo" />
        <p className="mx-auto mt-5 max-w-lg font-nunito text-[20px] font-medium text-[#707070]">
          Your Wealth, Your Way! <br />
          Invest in Equity, Mutual Fund, Bond, IPO, FD, Digital <br /> Gold,
          NPS, Unlisted Shares — all in one app.
        </p>
        <p className="mx-auto mt-6 max-w-lg font-nunito text-[24px] font-bold text-[#266985]">
          Explore it all with a tap and let our smart insights guide your
          journey.
        </p>
        <p className="mt-4 font-nunito text-[24px] font-semibold text-[#1DAB91]">
          Let’s grow your money, the fun way!
        </p>
      </div>

      {/* Right Section */}
      <div className="flex flex-1 flex-col items-center justify-center bg-white p-4 md:p-8">
        <div>
          <h2 className="text-center font-nunito text-[30px] font-bold text-[#266985] md:text-[50px]">
            Sign Up
          </h2>
          <p className="mb-6 mt-1 text-center font-nunito text-[20px] text-[#6A7378]">
            Welcome! Please enter your details
          </p>
        </div>

        <div className="w-full max-w-xl rounded-[30px] border border-[#6FC2EF45] bg-white px-5 py-5 shadow-[0_1px_20px_0_rgba(0,0,0,0.08)] md:p-[30px]">
          <form onSubmit={handleSubmit}>
            {/* 📱 Country + Mobile Field */}
            <label className="block font-nunito text-[16px] font-semibold text-[#707070]">
              Mobile Number
            </label>
            <div className="mt-2 flex gap-3 md:gap-4">
              {/* 🌍 Country Dropdown */}
              <select
                value={countryCode ?? ""}
                onChange={e => setCountryCode(e.target.value)}
                className="min-w-[90px] rounded-[6px] bg-[#f5f5f5] px-2 py-2.5 font-nunito text-[16px] font-semibold text-[#2F363F] outline-none focus:border-[#1AAE90]"
              >
                {countryCodes?.map(c => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>

              {/* 📞 Mobile Input */}
              <input
                type="tel"
                maxLength={10}
                placeholder="Enter your Mobile Number"
                value={mobile}
                onChange={handleMobileChange}
                className={`w-full rounded-[6px] border bg-[#f5f5f5] px-4 py-2.5 font-nunito text-[16px] font-bold text-[#2F363F] placeholder-[#2F363F] outline-none ${
                  error
                    ? "border-red-500 focus:border-red-500"
                    : "focus:border-[#1AAE90]"
                }`}
              />

              {/* OTP Button */}
              <button
                type="button"
                onClick={handleSendOtp}
                className="rounded-[6px] bg-gradient-to-l from-[#1AAE90] to-[#258996] px-4 py-2 font-nunito text-[16px] font-semibold text-white hover:bg-teal-700"
              >
                OTP
              </button>
            </div>

            {/* ⚠️ Error Message */}
            {error && (
              <p className="mt-2 font-nunito text-sm text-red-600">{error}</p>
            )}

            {/* OTP Section */}
            <p className="mt-5 text-center font-nunito text-[16px] font-semibold text-[#6A7378]">
              Enter the OTP sent to your phone number.
            </p>
            <div className="mt-3 flex justify-center gap-4 md:gap-5">
              {otp.map((digit, i) => (
                <input
                  key={digit}
                  ref={el => (otpRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => {
                    const val = e.target.value;
                    if (!/^\d?$/.test(val)) {
                      return;
                    }
                    const newOtp = [...otp];
                    newOtp[i] = val;
                    setOtp(newOtp);
                    if (val && i < otpRefs.current.length - 1) {
                      otpRefs.current[i + 1]?.focus();
                    }
                  }}
                  className="h-14 w-14 rounded-[6px] bg-[#F5F5F5] text-center font-nunito text-[30px] font-semibold text-[#266985] outline-none focus:border-2 focus:border-[#1AAE90]"
                />
              ))}
            </div>

            {/* Name Field */}
            <label className="mt-6 block font-nunito text-[16px] font-semibold text-[#707070]">
              First Name
            </label>
            <input
              type="text"
              placeholder="Enter your Name"
              value={name}
              onChange={e => setName(e.target.value)}
              className="mt-2 w-full rounded-[6px] bg-[#f5f5f5] px-4 py-2.5 font-nunito text-[16px] font-bold text-[#2F363F] placeholder-[#2F363F] outline-none focus:border-[#1AAE90]"
            />

            {/* Submit */}
            <button
              type="submit"
              className="mt-8 h-[50px] w-full rounded-md bg-gradient-to-r from-[#127B8D] to-[#10B981] py-2 font-nunito text-[18px] font-semibold tracking-[1px] text-white transition hover:opacity-95 sm:mt-16"
            >
              Submit
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
